import { useEffect, useRef } from 'react';

interface MouseEventHandlerParams {
  updateTooltipState: (x: number, y: number) => void;
  throttleDelay: number;
  lastPositionRef: React.MutableRefObject<{ x: number; y: number } | null>;
}

export const useMouseEvents = ({
  updateTooltipState,
  throttleDelay,
  lastPositionRef,
}: MouseEventHandlerParams) => {
  const rafRef = useRef<number | null>(null);
  const throttleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isThrottledRef = useRef(false);

  useEffect(() => {
    const handleMouse = (e: MouseEvent) => {
      lastPositionRef.current = { x: e.clientX, y: e.clientY };
      if (isThrottledRef.current) return;

      isThrottledRef.current = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      rafRef.current = requestAnimationFrame(() => {
        if (lastPositionRef.current) {
          updateTooltipState(
            lastPositionRef.current.x,
            lastPositionRef.current.y,
          );
        }
      });

      throttleTimerRef.current = setTimeout(() => {
        isThrottledRef.current = false;
      }, throttleDelay);
    };

    document.addEventListener('mousemove', handleMouse);

    return () => {
      document.removeEventListener('mousemove', handleMouse);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (throttleTimerRef.current) clearTimeout(throttleTimerRef.current);
      isThrottledRef.current = false;
    };
  }, [updateTooltipState, throttleDelay, lastPositionRef]);
};
